import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { BlackoutConfigService, TimeconversionService, ToastMessageService } from '@fitness-force/api';
import type { BlackoutConfig, CreateBlackoutConfigRequest, UpdateBlackoutConfigRequest } from '@fitness-force/models';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

interface DayOption {
  label: string;
  value: string;
  key: string;
}

interface TypeOption {
  label: string;
  value: 'Date' | 'Time';
}

@Component({
  selector: 'blackout-period-create',
  templateUrl: './blackout-period-create.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ToastMessageService],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    DatePickerModule,
    DialogModule,
    DividerModule,
    InputTextModule,
    RippleModule,
    SelectModule,
    TextareaModule,
    ToggleSwitchModule,
    TranslateModule,
  ],
})
export class BlackoutPeriodCreateComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly blackoutConfigService = inject(BlackoutConfigService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly timeConversion = inject(TimeconversionService);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly visible = input<boolean>(false);
  readonly activeConfig = input<BlackoutConfig | null>(null);
  readonly changed = output<void>();
  readonly dialogClose = output<void>();

  form!: UntypedFormGroup;
  tenantId = signal(0);
  isSubmitting = signal(false);
  isSecondaryLanguage = signal(false);
  primaryLanguage = signal('');
  secondaryLanguage = signal('');

  typeOptions: TypeOption[] = [
    { label: 'Date', value: 'Date' },
    { label: 'Time', value: 'Time' },
  ];

  dayOptions: DayOption[] = [
    { label: 'Mon', value: 'Mon', key: 'DAYS_MON' },
    { label: 'Tue', value: 'Tue', key: 'DAYS_TUE' },
    { label: 'Wed', value: 'Wed', key: 'DAYS_WED' },
    { label: 'Thu', value: 'Thu', key: 'DAYS_THU' },
    { label: 'Fri', value: 'Fri', key: 'DAYS_FRI' },
    { label: 'Sat', value: 'Sat', key: 'DAYS_SAT' },
    { label: 'Sun', value: 'Sun', key: 'DAYS_SUN' },
  ];

  selectedDays: string[] = [];

  constructor() {
    // Rebuild/repopulate the form every time the dialog opens (visible becomes true)
    // This ensures edit data is loaded after activeConfig is set by the parent
    effect(() => {
      const isVisible = this.visible();
      const config = this.activeConfig();
      if (isVisible && this.tenantId() > 0) {
        this.buildForm(config);
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(TENANTS_INFO),
      this.store.select(COMPANY_INFO),
    ]).subscribe({
      next: ([tenant]: [any, any]) => {
        this.tenantId.set(tenant.selected_tenant.id);
        if (this.tenantId() > 0) {
          if (tenant.selected_tenant?.secondary_language) {
            this.isSecondaryLanguage.set(true);
            this.translate
              .get([
                'common.LANGUAGE.' + tenant.selected_tenant.primary_language?.toUpperCase(),
                'common.LANGUAGE.' + tenant.selected_tenant.secondary_language?.toUpperCase(),
              ])
              .subscribe((translations) => {
                this.primaryLanguage.set(
                  translations['common.LANGUAGE.' + tenant.selected_tenant.primary_language?.toUpperCase()]
                );
                this.secondaryLanguage.set(
                  translations['common.LANGUAGE.' + tenant.selected_tenant.secondary_language?.toUpperCase()]
                );
              });
          }
          // Build a blank form on init so the template has a form reference immediately
          this.buildForm(null);
          this.cdr.detectChanges();
        }
      },
    });
  }

  buildForm(config: BlackoutConfig | null): void {
    this.selectedDays = config?.applies_to_days
      ? config.applies_to_days.split(',').map((d) => d.trim()).filter(Boolean)
      : [];

    this.form = this.fb.group({
      type: [
        config?.type
          ? this.typeOptions.find((t) => t.value === config.type) ?? null
          : this.typeOptions[0],
        Validators.required,
      ],
      name_pri_lang: [config?.name_pri_lang ?? '', Validators.required],
      name_sec_lang: [config?.name_sec_lang ?? ''],
      description_pri_lang: [config?.description_pri_lang ?? ''],
      description_sec_lang: [config?.description_sec_lang ?? ''],
      start_date: [config?.start_date ? this.timeConversion.ConvertUTCToLocalDateTime(config.start_date) : null],
      end_date: [config?.end_date ? this.timeConversion.ConvertUTCToLocalDateTime(config.end_date) : null],
      is_recurring: [config?.is_recurring ?? false],
      start_time: [config?.start_time ? this.timeStringToDate(config.start_time) : null],
      end_time: [config?.end_time ? this.timeStringToDate(config.end_time) : null],
    });
  }

  get TypeControl(): AbstractControl | null {
    return this.form?.get('type');
  }

  get NamePrimary(): AbstractControl | null {
    return this.form?.get('name_pri_lang');
  }

  get NameSecondary(): AbstractControl | null {
    return this.form?.get('name_sec_lang');
  }

  get SelectedType(): 'Date' | 'Time' | null {
    return this.TypeControl?.value?.value ?? null;
  }

  onTypeChange(): void {
    if (this.SelectedType === 'Date') {
      this.selectedDays = [];
      this.form?.patchValue({ start_time: null, end_time: null });
    } else if (this.SelectedType === 'Time') {
      this.form?.patchValue({ start_date: null, end_date: null });
    }
  }

  toggleDay(day: string): void {
    const idx = this.selectedDays.indexOf(day);
    if (idx >= 0) {
      this.selectedDays = this.selectedDays.filter((d) => d !== day);
    } else {
      this.selectedDays = [...this.selectedDays, day];
    }
  }

  isDaySelected(day: string): boolean {
    return this.selectedDays.includes(day);
  }

  submit(): void {
    if (!this.form.valid) {
      Object.keys(this.form.controls).forEach((field) => {
        this.form.get(field)?.markAsTouched({ onlySelf: true });
      });
      return;
    }

    const raw = this.form.getRawValue();
    const payload: CreateBlackoutConfigRequest | UpdateBlackoutConfigRequest = {
      type: raw.type.value as 'Date' | 'Time',
      name_pri_lang: raw.name_pri_lang,
      name_sec_lang: raw.name_sec_lang || null,
      description_pri_lang: raw.description_pri_lang || null,
      description_sec_lang: raw.description_sec_lang || null,
      start_date: raw.start_date ? this.timeConversion.ConvertLocalDateTimeToUTC(raw.start_date as Date) : null,
      end_date: raw.end_date ? this.timeConversion.ConvertLocalDateTimeToUTC(raw.end_date as Date) : null,
      is_recurring: raw.type?.value === 'Date' ? raw.is_recurring : false,
      start_time: raw.start_time ? this.dateToTimeString(raw.start_time as Date) : null,
      end_time: raw.end_time ? this.dateToTimeString(raw.end_time as Date) : null,
      applies_to_days: raw.type?.value === 'Time' && this.selectedDays.length > 0 ? this.selectedDays.join(',') : null,
    };

    const config = this.activeConfig();
    this.isSubmitting.set(true);

    if (config) {
      this.blackoutConfigService
        .updateBlackoutConfig(this.tenantId(), config.config_id, payload as UpdateBlackoutConfigRequest)
        .subscribe({
          next: () => {
            this.isSubmitting.set(false);
            this.translate.get('CAMPAIGN.BLACKOUT.UPDATED_SUCCESS').subscribe((msg) => {
              this.toastMessageService.showSuccess(msg);
            });
            this.resetForm();
            this.changed.emit();
          },
          error: (err: unknown) => {
            this.isSubmitting.set(false);
            this.toastMessageService.showError(err);
          },
        });
    } else {
      this.blackoutConfigService
        .createBlackoutConfig(this.tenantId(), payload as CreateBlackoutConfigRequest)
        .subscribe({
          next: () => {
            this.isSubmitting.set(false);
            this.translate.get('CAMPAIGN.BLACKOUT.CREATED_SUCCESS').subscribe((msg) => {
              this.toastMessageService.showSuccess(msg);
            });
            this.resetForm();
            this.changed.emit();
          },
          error: (err: unknown) => {
            this.isSubmitting.set(false);
            this.toastMessageService.showError(err);
          },
        });
    }
  }

  cancel(): void {
    this.resetForm();
    this.dialogClose.emit();
  }

  resetForm(): void {
    this.selectedDays = [];
    this.form?.reset({
      type: null,
      name_pri_lang: '',
      name_sec_lang: '',
      description_pri_lang: '',
      description_sec_lang: '',
      start_date: null,
      end_date: null,
      is_recurring: false,
      start_time: null,
      end_time: null,
    });
    this.form?.markAsPristine();
    this.form?.markAsUntouched();
  }

  /** Convert "HH:mm" or "HH:mm:ss" string from API into a Date for p-datepicker timeOnly */
  private timeStringToDate(time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const d = new Date();
    d.setHours(hours ?? 0, minutes ?? 0, 0, 0);
    return d;
  }

  /** Convert a Date (from p-datepicker timeOnly) to "HH:mm" string without UTC conversion */
  private dateToTimeString(date: Date): string {
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

}
